// My books › Owned copies (Phase 6.4 Part 3; agreed with Kristi Oct 31, laptop mockup in chat):
// the format chips, the Where chips, the list's columns, sort and paging, and each row's one
// action. No database calls here, so the rules are tested on their own
// (tests/logic/my-books-owned.test.ts).

import type { Precision } from '../book/types';
import { dayText, fuzzyText } from '../book/model';
import { num } from '../queue/model';

/** One copy you own, share or have lent, lost or given away (never a library copy). */
export interface OwnedCopy {
  id: string;
  bookId: string;
  workId: string;
  /** This copy's own edition's cover, when it has one (else the book's, shown as on the shelves). */
  editionCover: string | null;
  label: number;
  title: string;
  author: string;
  format: string;
  /** An audiobook edition (an Audible copy, or a Libro.fm one). */
  listen: boolean;
  how: 'bought' | 'gift' | 'shared' | 'free';
  state: 'have_it' | 'lent_out' | 'lost' | 'donated';
  acquiredOn: string | null;
  acquiredPrecision: Precision | null;
  acquiredWords: string | null;
  store: string | null;
  price: number | null;
  roomId: string | null;
  shelf: string | null;
  lentTo: string | null;
  lentOn: string | null;
  /** A gift's giver or a free copy's place (0047). */
  gotFrom?: string | null;
  asin: string | null;
  audibleId: string | null;
}

export interface Room {
  id: string;
  name: string;
  order: number;
}

// ---------- format chips ----------

/**
 * The format filter chips on top (consistency pass R3, Kristi Nov 2; were single-pick tiles):
 * several can be on at once, none on means every format, and a format with no copies is hidden
 * until it has some. Shared, lent, lost or donated and missing IDs are rows in the Filters panel.
 */
export type Format = 'kindle' | 'audible' | 'paper' | 'pdf_epub' | 'other';

/** In this order. */
export const FORMATS: Format[] = ['kindle', 'audible', 'paper', 'pdf_epub', 'other'];

/** Kristi, Oct 31: the tab opens on Paper (where Where matters). */
export const START_FORMATS: Format[] = ['paper'];

/** A remembered choice, kept to the formats there are (an old single-pick tile becomes a list). */
export function cleanFormats(v: unknown): Format[] {
  const list = Array.isArray(v) ? v : typeof v === 'string' ? (v === 'all' ? [] : [v]) : START_FORMATS;
  return FORMATS.filter((f) => list.includes(f));
}

const FORMAT_KEYS: Partial<Record<string, Format>> = { kindle: 'kindle', audible: 'audible', paper: 'paper', pdf_epub: 'pdf_epub' };
export const gone = (c: OwnedCopy) => c.state === 'lost' || c.state === 'donated';

/** A Kindle or Audible copy you have with no Kindle or Audible ID on its edition. */
export const missingId = (c: OwnedCopy) =>
  !gone(c) && ((c.format === 'kindle' && !c.asin) || (c.format === 'audible' && !c.audibleId));

/** Whether a copy is of a format (lost and donated copies included; the filters decide those). */
export function inFormat(c: OwnedCopy, f: Format): boolean {
  if (f === 'other') return !FORMAT_KEYS[c.format];
  return FORMAT_KEYS[c.format] === f;
}

/** Whether a copy is of any of the chosen formats; none chosen means every format. */
export const inFormats = (c: OwnedCopy, fs: Format[]) => !fs.length || fs.some((f) => inFormat(c, f));

/** Formats whose copies are all digital: no Where column while only these are on. */
export const DIGITAL_FORMATS: Format[] = ['kindle', 'audible', 'pdf_epub'];
export const digitalOnly = (fs: Format[]) => fs.length > 0 && fs.every((f) => DIGITAL_FORMATS.includes(f));

/** Mark lost / Mark donated: paper copies you still have, never digital ones (Kristi, Oct 31). */
export const canMarkGone = (c: OwnedCopy) => c.format === 'paper' && !gone(c);

export const FORMAT_NAMES_SHORT: Record<Format, string> = {
  kindle: 'Kindle',
  audible: 'Audible',
  paper: 'Paper',
  pdf_epub: 'PDF / EPUB',
  other: 'Other',
};

// ---------- worth a look (filters review, Kristi Nov 2; was "what needs you", Oct 31) ----------
// A paper copy with no Where, a Kindle or Audible copy with no ID, a copy lent 3 months or more:
// optional tidying, as ringed signal chips (SIGNALS below), never a tab dot or a teal edge.

/** Lent 3+ months: lent on or before this day, 3 months before today. */
export function lentLongBefore(today: string): string {
  if (!today) return '';
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 3);
  return d.toISOString().slice(0, 10);
}
export const lentLong = (c: OwnedCopy, today: string) => c.state === 'lent_out' && Boolean(today) && c.lentOn !== null && c.lentOn <= lentLongBefore(today);

/**
 * The format chips, in order, with their counts: copies you still have, within the Filters panel's
 * result (R7 N3; lost and donated show only when State asks for them). A chip whose count is 0 is
 * hidden by FilterChip unless it's on. No dots: the signals have their own chips (filters review).
 */
export function formatChips(copies: OwnedCopy[], rooms: Room[] = [], today = '', filters: OwnedFilters = {}): { key: Format; name: string; count: number }[] {
  const count = (f: Format) => applyOwnedFilters(copies.filter((c) => inFormat(c, f)), filters, rooms, today).length;
  return FORMATS.map((key) => ({ key, name: FORMAT_NAMES_SHORT[key], count: count(key) }));
}

// ---------- the Filters panel ----------

/** "Not placed yet" as a Where chip. */
export const NOT_PLACED = 'none';

export type OwnedGroupKey = 'where' | 'how' | 'state' | 'ids';
export interface OwnedChip {
  id: string;
  label: string;
  count: number;
  /** A ring: worth a look (filters review F2, Kristi Nov 2: nothing on Owned copies needs you). */
  worth?: boolean;
  /** The ring's tooltip ("Worth a look: 3 not placed yet"; R7 N9). */
  tip?: string;
}
/**
 * Owned copies' signals (filters review, Kristi Nov 2): each is worth a look, never needs you, so no
 * tab dot and no teal edge on its rows. Each chip also sits in its Filters row (F4).
 */
export const SIGNALS: { group: OwnedGroupKey; id: string; label: string; tip: (n: number) => string }[] = [
  { group: 'where', id: NOT_PLACED, label: 'Not placed yet', tip: (n) => `Worth a look: ${num(n)} not placed yet` },
  { group: 'ids', id: 'missing_id', label: 'Missing an ID', tip: (n) => `Worth a look: ${num(n)} missing an ID` },
  { group: 'state', id: 'lent_long', label: 'Lent 3+ months', tip: (n) => `Worth a look: ${num(n)} lent 3+ months` },
];
const signalOf = (g: OwnedGroupKey, id: string) => SIGNALS.find((x) => x.group === g && x.id === id);
export interface OwnedGroup {
  key: OwnedGroupKey;
  row: string;
  label: string;
  /** Show / Hide on rows of kinds (Where, How), not of conditions (State, IDs) (F8). */
  hideable: boolean;
  chips: OwnedChip[];
  empty?: string;
}
export type OwnedFilters = Partial<Record<OwnedGroupKey, { mode: 'show' | 'hide'; on: string[] }>>;

const unplaced = (c: OwnedCopy, rooms: Room[]) => !c.roomId || !rooms.some((r) => r.id === c.roomId);

/** The values a copy has for one row of the panel. */
export function ownedValues(c: OwnedCopy, g: OwnedGroupKey, rooms: Room[], today = ''): string[] {
  switch (g) {
    case 'where':
      // Only paper copies you still have have a place; a lost or donated copy keeps its room for
      // I still have it, but never counts as placed or Not placed yet (Kristi, Oct 31).
      return c.format !== 'paper' || gone(c) ? [] : unplaced(c, rooms) ? [NOT_PLACED] : [c.roomId!];
    case 'how':
      return [c.how];
    case 'state':
      return gone(c) ? ['gone'] : lentLong(c, today) ? [c.state, 'lent_long'] : [c.state];
    case 'ids':
      return missingId(c) ? ['missing_id'] : [];
  }
}

const withoutRow = (f: OwnedFilters, g: OwnedGroupKey): OwnedFilters => {
  const { [g]: _drop, ...rest } = f;
  return rest;
};

/**
 * The panel's rows for the chosen formats (Kristi, Oct 31): Where (your rooms holding a paper copy, then Not
 * placed yet) · How (Bought · Gift · Shared · Free) · State (Have it · Lent out · Lost · donated) · IDs
 * (Missing an ID). Each chip counts within what the other rows leave (F10, Kristi Nov 2); chips with
 * none are left out unless they're on.
 */
export function ownedGroups(copies: OwnedCopy[], formats: Format[], rooms: Room[], today = '', filters: OwnedFilters = {}): OwnedGroup[] {
  const inT = copies.filter((c) => inFormats(c, formats));
  const pool = (g: OwnedGroupKey) => {
    const rest = withoutRow(filters, g);
    // State's own chips include lost and donated; the other rows count copies you still have.
    const list = g === 'state' ? inT : inT.filter((c) => !gone(c));
    return Object.keys(rest).length ? applyOwnedFilters(list, rest, rooms, today, g === 'state') : list;
  };
  const chips = (g: OwnedGroupKey, ids: [string, string][]) => {
    const list = pool(g);
    const on = filters[g]?.on ?? [];
    return ids
      .map(([id, label]) => {
        const n = list.filter((c) => ownedValues(c, g, rooms, today).includes(id)).length;
        const sig = signalOf(g, id);
        return { id, label, count: n, ...(sig ? { worth: true, tip: sig.tip(n) } : {}) };
      })
      .filter((c) => c.count > 0 || on.includes(c.id));
  };
  const roomIds: [string, string][] = [...rooms].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)).map((r) => [r.id, r.name]);
  return [
    { key: 'where', row: 'Where', label: 'Where', hideable: true, chips: chips('where', [...roomIds, [NOT_PLACED, 'Not placed yet']]), empty: 'Paper copies only' },
    { key: 'how', row: 'How', label: 'How', hideable: true, chips: chips('how', [['bought', 'Bought'], ['gift', 'Gift'], ['shared', 'Shared with me'], ['free', 'Free']]) },
    { key: 'state', row: 'State', label: 'State', hideable: false, chips: chips('state', [['have_it', 'Have it'], ['lent_out', 'Lent out'], ['lent_long', 'Lent 3+ months'], ['gone', 'Lost · donated']]) },
    { key: 'ids', row: 'IDs', label: 'IDs', hideable: false, chips: chips('ids', [['missing_id', 'Missing an ID']]), empty: 'Every Kindle and Audible copy has its ID' },
  ];
}

/**
 * The signal chips at the top of the tab (F1, F3): across every format, within what the Filters
 * panel's other rows leave; one at [0] isn't shown unless it's on.
 */
export function signalChips(copies: OwnedCopy[], rooms: Room[], today: string, filters: OwnedFilters) {
  return SIGNALS.map((sig) => {
    const rest = withoutRow(filters, sig.group);
    const list = applyOwnedFilters(copies, rest, rooms, today);
    const n = list.filter((c) => ownedValues(c, sig.group, rooms, today).includes(sig.id)).length;
    const on = filters[sig.group]?.mode !== 'hide' && (filters[sig.group]?.on.includes(sig.id) ?? false);
    return { ...sig, count: n, on, tipText: sig.tip(n) };
  });
}

/**
 * Rows must all match; within a row, any chip (Show only) or none of them (Hide). Lost and donated
 * copies show only when State asks for them (or `keepGone`, for State's own counts).
 */
export function applyOwnedFilters(list: OwnedCopy[], f: OwnedFilters, rooms: Room[], today = '', keepGone = false): OwnedCopy[] {
  const wantsGone = keepGone || (f.state?.on.includes('gone') ?? false);
  return list.filter((c) => {
    if (gone(c) && !wantsGone) return false;
    for (const g of Object.keys(f) as OwnedGroupKey[]) {
      const s = f[g];
      if (!s?.on.length) continue;
      const any = ownedValues(c, g, rooms, today).some((v) => s.on.includes(v));
      if (s.mode === 'hide' ? any : !any) return false;
    }
    return true;
  });
}

// ---------- where ----------

export const roomName = (c: OwnedCopy, rooms: Room[]) => rooms.find((r) => r.id === c.roomId)?.name ?? null;

/** "Studio · Bookcase 2 · top", "Studio", or null when the copy isn't placed. */
export function whereText(c: OwnedCopy, rooms: Room[], withShelf = true): string | null {
  const room = roomName(c, rooms);
  const parts = [room, withShelf ? c.shelf : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

// ---------- the list ----------

export type SortKey = 'title' | 'author' | 'format' | 'where' | 'acquired' | 'how' | 'from' | 'store' | 'price' | 'shelf';
export type Extra = 'how' | 'from' | 'store' | 'price' | 'shelf';

/** The Columns ▾ picker (Kristi, Oct 31): How, From (a gift's giver, a free copy's place), Store, Price and Shelf, off to start. */
export const EXTRA_COLUMNS: { key: Extra; name: string }[] = [
  { key: 'how', name: 'How' },
  { key: 'from', name: 'From' },
  { key: 'store', name: 'Store' },
  { key: 'price', name: 'Price' },
  { key: 'shelf', name: 'Shelf' },
];

export const SORT_NAMES: Record<SortKey, string> = {
  title: 'Title',
  author: 'Author',
  format: 'Format',
  where: 'Where',
  acquired: 'Acquired',
  how: 'How',
  from: 'From',
  store: 'Store',
  price: 'Price',
  shelf: 'Shelf',
};

const HOW_NAMES = { bought: 'Bought', gift: 'Gift', shared: 'Shared', free: 'Free' } as const;
export const howName = (c: OwnedCopy) => HOW_NAMES[c.how] ?? c.how;

const FORMAT_NAMES: Record<string, string> = {
  kindle: 'Kindle',
  audible: 'Audible',
  paper: 'Paper',
  apple_books: 'Apple Books',
  google_play: 'Google Play',
  kobo: 'Kobo',
  librofm: 'Libro.fm',
  hoopla: 'Hoopla',
  pdf_epub: 'PDF / EPUB',
  article: 'Article',
  other: 'Other',
};
export const formatText = (c: OwnedCopy) => FORMAT_NAMES[c.format] ?? c.format;

/** Titles file without a leading The, A or An (as on Reference). */
export const fileTitle = (t: string) => t.replace(/^(the|a|an)\s+/i, '').toLowerCase();

export const acquiredText = (c: OwnedCopy) => (c.acquiredOn || c.acquiredWords ? fuzzyText(c.acquiredOn, c.acquiredPrecision, c.acquiredWords) : '');

export const priceText = (c: OwnedCopy) => (c.price === null ? '' : `$${c.price.toFixed(2)}`);

/** Sorted by a column, then title; empty values always last, whichever way. */
export function sortCopies(copies: OwnedCopy[], key: SortKey, desc: boolean, rooms: Room[]): OwnedCopy[] {
  const val = (c: OwnedCopy): string | number | null => {
    switch (key) {
      case 'title':
        return fileTitle(c.title);
      case 'author':
        return c.author ? c.author.toLowerCase() : null;
      case 'format':
        return formatText(c).toLowerCase();
      case 'where':
        return whereText(c, rooms)?.toLowerCase() ?? null;
      case 'acquired':
        return c.acquiredOn;
      case 'how':
        return howName(c).toLowerCase();
      case 'from':
        return c.gotFrom ? c.gotFrom.toLowerCase() : null;
      case 'store':
        return c.store ? c.store.toLowerCase() : null;
      case 'price':
        return c.price;
      case 'shelf':
        return c.shelf ? c.shelf.toLowerCase() : null;
    }
  };
  const tie = (a: OwnedCopy, b: OwnedCopy) => fileTitle(a.title).localeCompare(fileTitle(b.title)) || a.label - b.label || a.id.localeCompare(b.id);
  return [...copies].sort((a, b) => {
    const x = val(a);
    const y = val(b);
    if (x === null && y === null) return tie(a, b);
    if (x === null) return 1;
    if (y === null) return -1;
    const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return (desc ? -c : c) || tie(a, b);
  });
}

/** What the list shows: the chosen formats' copies (every format when none is on), narrowed by the Filters panel. */
export function shownCopies(copies: OwnedCopy[], formats: Format[], filters: OwnedFilters, rooms: Room[], today = ''): OwnedCopy[] {
  return applyOwnedFilters(
    copies.filter((c) => inFormats(c, formats)),
    filters,
    rooms,
    today,
  );
}

export const PAGE = 20;

// ---------- a row's one action ----------

export type RowAction =
  | { kind: 'still_have' }
  | { kind: 'got_back'; line: string }
  | { kind: 'lend' }
  | { kind: 'open'; label: string; url: string }
  | { kind: 'add_id'; label: string }
  | { kind: 'none' };

/**
 * One action per row (board 8): I still have it for a lost or donated copy; Got it back for a
 * lent one (with who and since when); Lend it… for paper; Open in … for a digital copy with its
 * ID (… ↗: it leaves the app); a dashed "No Kindle ID ▾" / "No Audible ID ▾" with a paste box when the ID is missing (Kristi, Nov 2).
 */
export function rowAction(c: OwnedCopy, phone: boolean, today?: string): RowAction {
  if (gone(c)) return { kind: 'still_have' };
  if (c.state === 'lent_out') {
    const who = c.lentTo ? `Lent to ${c.lentTo}` : 'Lent out';
    return { kind: 'got_back', line: c.lentOn ? `${who} · since ${dayText(c.lentOn, today)}` : who };
  }
  if (c.format === 'paper') return { kind: 'lend' };
  if (c.format === 'kindle') {
    if (!c.asin) return { kind: 'add_id', label: 'No Kindle ID' };
    return {
      kind: 'open',
      label: 'Open in Kindle ↗',
      url: phone
        ? `https://read.amazon.com/application/home/?_encoding=UTF8&action=open&asin=${c.asin}&fallbackToAppDownloadPage=true`
        : `https://read.amazon.com/?asin=${c.asin}`,
    };
  }
  if (c.format === 'audible') {
    if (!c.audibleId) return { kind: 'add_id', label: 'No Audible ID' };
    return {
      kind: 'open',
      label: phone ? 'Open in Audible ↗' : 'Play in Audible ↗',
      url: phone ? `https://www.audible.com/pd/${c.audibleId}` : `https://www.amazon.com/arya/webplayer?marketplaceId=AF2M0KC94RCEA&asin=${c.audibleId}`,
    };
  }
  return { kind: 'none' };
}

/** Names you've lent to before, most recent first, for Lend it…'s suggestions. */
export function lentNames(copies: OwnedCopy[]): string[] {
  const seen = new Map<string, string>();
  for (const c of [...copies].filter((x) => x.lentTo).sort((a, b) => (b.lentOn ?? '').localeCompare(a.lentOn ?? ''))) {
    const k = c.lentTo!.toLowerCase();
    if (!seen.has(k)) seen.set(k, c.lentTo!);
  }
  return [...seen.values()].slice(0, 6);
}
